import {PlayerBadge, PlayerId, usePlayers} from '@glark-newco/game-library';
import {Center, Grid, Paper, Text, Title} from '@mantine/core';
import {ReactNode, useMemo} from 'react';
import {SupplyChainAvatarProvider} from './SupplyChainAvatar';
import {useFirstPlayerComplete, useObjectivesStats, usePlayerStats} from '../hooks/useObjectivesComplete';
import {useMostActivePlayer} from '../state/PlayerActivitySlice';
import {useAverageTransfersPerRing, useMostActiveRing} from '../state/RingStatisticsSlice';


//////////////////////////////////////////////////
///  Helpers

function title(titleText: string, subText?: string) {
    return <>
        <Title order={3}>{titleText}</Title>
        <Text>{subText}</Text>
    </>;
}

function useFindPlayer(playerId: string | undefined) {
    const {players} = usePlayers();
    return useMemo(() => {
        return players.find(p => p.playerId === playerId);
    }, [playerId, players]);
}

function MaybePlayer({playerId}: {playerId: PlayerId | undefined}): ReactNode {
    const found = useFindPlayer(playerId);
    return !!found &&
        <PlayerBadge player={found} avatar={SupplyChainAvatarProvider} w={'45%'} display={'inline-flex'}/>;
}

//////////////////////////////////////////////////
///  Sub-components

function MostActiveRing() {
    const {transferCount, firstOwner, currentOwner} = useMostActiveRing();

    if (transferCount === 0)
        return <></>;

    return <>
        <MaybePlayer playerId={firstOwner}/>
        <Text display={'inline-flex'} fw={900} m={'1em'} bottom={'0.5em'} pos={'relative'}>--►</Text>
        <MaybePlayer playerId={currentOwner}/>
    </>;
}

function MostActivePlayer() {
    const {playerId, transferCount} = useMostActivePlayer();

    if (!playerId)
        return <></>;

    return <>
        <MaybePlayer playerId={playerId}/>
        <Text  display={'inline-flex'} m={'1em'} bottom={'0.5em'} pos={'relative'}>({transferCount} transfers)</Text>
    </>;
}

function AverageTransfers() {
    const averageTransfersPerRing = useAverageTransfersPerRing();
    return <Text>{averageTransfersPerRing.toLocaleString('en-UK', {maximumSignificantDigits: 2})} transfers per Ring</Text>;
}

function FirstExcluded() {
    const playerId = useFirstPlayerComplete();
    return <MaybePlayer playerId={playerId}/>;
}

//////////////////////////////////////////////////
///  Panel

export function ReviewStatsPanel() {
    const {allObjectivesCount, completeObjectivesCount} = useObjectivesStats();
    const {allPlayersCount, completePlayersCount} = usePlayerStats();

    const completeObjectivesPercent = !allObjectivesCount ? 0 : completeObjectivesCount / allObjectivesCount * 100;
    const completePlayersPercent = !allPlayersCount ? 0 : completePlayersCount / allPlayersCount * 100;


    return <>
        <Center style={{height: '100%'}} p={'xl'}>
            <Paper withBorder p={'xl'} radius={'xl'} w={'60%'} h={'57%'} style={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: '#ccc',
            }}>
                <Center mb={'xl'}><Title order={2}>Supply Chain summary</Title></Center>
                <Grid columns={5} mb={'xl'}>
                    <Grid.Col span={2}>{title('Number of successful players')}</Grid.Col>
                    <Grid.Col span={1}><Center>
                        <Text size="xl" fw={700}
                            data-testid={'players_count'}>{completePlayersCount.toString()}/{allPlayersCount.toString()}</Text>
                        <Text size="xl" pl={'md'}
                            data-testid={'players_percent'}>({completePlayersPercent.toLocaleString('en-UK', {maximumSignificantDigits: 2})}%)</Text>
                    </Center></Grid.Col>
                    <Grid.Col span={1}></Grid.Col>

                    <Grid.Col span={2}>{title('Total completed objectives')}</Grid.Col>
                    <Grid.Col span={1}><Center>
                        <Text size="xl" fw={700}
                            data-testid={'objectives_count'}>{completeObjectivesCount.toString()}/{allObjectivesCount.toString()}</Text>
                        <Text size="xl" pl={'md'}
                            data-testid={'objectives_percent'}>({completeObjectivesPercent.toLocaleString('en-UK', {maximumSignificantDigits: 2})}%)</Text>
                    </Center></Grid.Col>
                </Grid>

                <Grid columns={5}>
                    <Grid.Col span={2}>{title('Longest ring journey', 'largest number of transfers')}</Grid.Col>
                    <Grid.Col span={3} data-testid={'active_ring'}><MostActiveRing/></Grid.Col>

                    <Grid.Col span={2}>{title('Most active player', 'transferred the most rings')}</Grid.Col>
                    <Grid.Col span={3} data-testid={'active_player'}><MostActivePlayer/></Grid.Col>

                    <Grid.Col span={2}>{title('Average transfers per ring', 'distance each ring travelled')}</Grid.Col>
                    <Grid.Col span={3} data-testid={'average_transfers'}><AverageTransfers/></Grid.Col>

                    <Grid.Col
                        span={2}>{title('First player excluded', 'first player to complete their Personal Objective')}</Grid.Col>
                    <Grid.Col span={3} data-testid={'first_excluded'}><FirstExcluded/></Grid.Col>
                </Grid>
            </Paper>
        </Center>
    </>;
}
